import { TableColumnType } from 'components';
import { AuthContext } from 'contexts';
import { useBreakpoint } from 'hooks';
import React, { useContext, useMemo } from 'react';
import { StatusEntry } from '../../../components';
import { useShowingsCalendarContext } from '../ShowingsCalendarContext';
import { renderCoopContactEntry, buildTableComponents } from './tableUtils';
import {
  CommonInnerTableProps,
  VirtualTableData,
  TableRenderFn,
} from '../types';
import { StyledInnerTable, TimeEntry, StyledMobileColumn } from './components';

const VirtualTable: React.FC<CommonInnerTableProps<VirtualTableData>> = ({
  tableKey,
  showings,
}) => {
  const { auth } = useContext(AuthContext);
  const { listing } = useShowingsCalendarContext();
  const { md } = useBreakpoint();

  const renderTimeEntry: TableRenderFn<VirtualTableData[number]> = (
    _,
    { time, listing: { timezone } },
    i,
    opts,
  ) => (
    <TimeEntry
      time={time}
      timezone={timezone}
      needsAction={false}
      showEnd={false}
      isMobile={opts?.isMobile}
    />
  );

  const renderStatusEntry: TableRenderFn<VirtualTableData[number]> = (
    _,
    { status, displayStatus },
  ) => <StatusEntry isBuyer={false} displayStatus={displayStatus} />;

  const renderMobile: TableRenderFn<VirtualTableData[number]> = (
    _,
    appointment,
    i,
  ) => (
    <StyledMobileColumn>
      {[
        renderStatusEntry(_, appointment, i),
        renderTimeEntry(_, appointment, i, { isMobile: true }),
        renderCoopContactEntry(_, appointment, i, { isMobile: true }),
      ]}
    </StyledMobileColumn>
  );

  const columns: TableColumnType<VirtualTableData[number]>[] = useMemo(
    () =>
      md
        ? // Desktop View
          [
            {
              key: 'time',
              dataIndex: '',
              width: 225,
              render: renderTimeEntry,
            },
            {
              key: 'status',
              dataIndex: '',
              width: 250,
              render: renderStatusEntry,
            },
            {
              key: 'coop',
              dataIndex: 'coop',
              render: renderCoopContactEntry,
            },
          ]
        : // Mobile View
          [
            {
              key: 'mobile',
              dataIndex: '',
              render: renderMobile,
            },
          ],
    [auth, listing, md],
  );

  return (
    <StyledInnerTable
      key={tableKey}
      components={buildTableComponents({ md })}
      columns={columns}
      rowKey={record => record._id}
      showHeader={false}
      dataSource={showings}
      pagination={false}
      data-cy="virtual-table"
    />
  );
};

export default VirtualTable;
